import yaml from "js-yaml"
import Vue from "vue"
import { caseTypeBucketDefaults } from "../helpers"

const noCurrentCaseType = (state) => !state.current.id
const existingCaseTypeInUse = (state) => !!state.current.id

const sampleSchemaObj = {
  name: "New Template",
  events: null,
  primary_key: "_ref",
  case_data_views: {
    new: {
      rows: [{ columns: [{ fieldset: "summary" }] }],
    },
    edit: {
      rows: [{ columns: [{ fieldset: "summary" }] }],
    },
  },
  fieldsets: [
    {
      name: "summary",
      fields: [{ name: "title", type: "string", required: true }],
    },
    {
      name: "internal_references",
      fields: [{ name: "_ref", type: "number", readonly: true, sequence: true }],
    },
  ],
}

const newTemplate = { name: "New Template", description: "", coreTemplate: true, iconCode: "folder", iconSet: "fas", currentSchema: sampleSchemaObj }

const setCurrentCaseTemplate = (state, { caseTypeId }) => {
  if (caseTypeId !== "new") {
    state.current = state.all.find((ct) => ct.id === parseInt(caseTypeId))
  } else {
    if (noCurrentCaseType(state) || existingCaseTypeInUse(state)) {
      state.current = newTemplate
    }
  }
}

const forceCurrentCaseTemplate = (state, caseType) => {
  state.current = caseType
}

const setCurrentCaseTemplateSchema = (state, schema) => {
  const schemaObj = yaml.safeLoad(schema)
  state.current.currentSchema = schemaObj
}

const setCurrentCaseTemplateRelationships = (state, relationships) => {
  Vue.set(state.current, "relationships", [...relationships])
}

const deleteRelationshipDisplayField = (state, data) => {
  const currentRelationships = state.current.relationships
  const relationship = currentRelationships.find((r) => r.id === data.relationId)

  if (relationship) {
    const targetFieldIndex = relationship.fields.findIndex((f) => f.id === data.id)

    if (targetFieldIndex) {
      relationship.fields.splice(targetFieldIndex, 1)
      Vue.set(state.current, "relationships", [...currentRelationships])
    }
  }
}

const createBucket = (state, bucket) => {
  state.current.buckets.push(bucket)
  Vue.set(state.current, "buckets", [...state.current.buckets])
}
const deleteBucket = (state, { caseTypeId, bucketId }) => {
  const caseType = state.all.find((ct) => ct.id === caseTypeId)
  if (caseType) {
    const index = caseType.buckets.findIndex((b) => b.id === bucketId)
    if (index) {
      caseType.buckets.splice(index, 1)
    }
  }
}

const setBucket = (state, bucket) => {
  if (bucket && bucket.id) {
    const caseTypeName = bucket.caseTypeName
    const bucketId = bucket.id
    if (state.all && Array.isArray(state.all)) {
      const caseType = state.all.find((ct) => ct.name === caseTypeName)
      if (!caseType || !caseType.buckets) return
      const bucketToUpdate = caseType.buckets.find((b) => b.id === bucketId)
      if (!bucketToUpdate) return
      if (bucket && bucket.contents && bucket.contents.statistics) {
        if (bucketToUpdate.contents === undefined) {
          bucketToUpdate.contents = {}
        }
        bucketToUpdate.contents.statistics = bucket.contents.statistics
      }
    }
  }
}

const SAVED_SCRIPT_FIELDS = [
  "name",
  "description",
  "createdAt",
  "updatedAt",
  "draftCode",
  "publishedCode",
  "draftDocument",
  "reusable",
  "isMicroservice",
  "runtimeType",
  "runtimeLanguage",
  "runtimeImage",
]

function scriptStamp(script) {
  if (!script || !script.updatedAt) return 0
  const stamp = Date.parse(script.updatedAt)
  return Number.isNaN(stamp) ? 0 : stamp
}

function rememberScripts(caseTypes, into) {
  ;(caseTypes || []).forEach((caseType) => {
    const scripts = caseType && caseType.scripts
    ;(scripts || []).forEach((script) => {
      if (!script || script.id == null) return
      const key = String(script.id)
      const prev = into[key]
      if (!prev || scriptStamp(script) >= scriptStamp(prev)) into[key] = script
    })
  })
}

function mergeIncomingScripts(incoming, remembered) {
  return (incoming || []).map((script) => {
    if (!script || script.id == null) return script
    const prev = remembered[String(script.id)]
    if (prev && scriptStamp(prev) >= scriptStamp(script)) return prev
    return script
  })
}

function copySavedScriptFields(target, source) {
  SAVED_SCRIPT_FIELDS.forEach((key) => {
    if (source[key] !== undefined) Vue.set(target, key, source[key])
  })
}

function applySavedScript(caseType, script) {
  if (!caseType || !Array.isArray(caseType.scripts) || !script || script.id == null) return
  const idx = caseType.scripts.findIndex((row) => parseInt(row.id, 10) === parseInt(script.id, 10))
  if (idx < 0) return
  copySavedScriptFields(caseType.scripts[idx], script)
}

export default {
  setCurrentCaseTemplate,
  setCurrentCaseTemplateSchema,
  setCurrentCaseTemplateRelationships,
  deleteRelationshipDisplayField,
  forceCurrentCaseTemplate,
  createBucket,
  deleteBucket,
  setBucket,
  setCaseTypes(state, caseTypes) {
    const list = caseTypes || []
    const remembered = {}
    rememberScripts(state.all, remembered)
    if (state.current) rememberScripts([state.current], remembered)
    for (const caseType of list) {
      caseTypeBucketDefaults(caseType)
      if (Array.isArray(caseType.scripts)) {
        caseType.scripts = mergeIncomingScripts(caseType.scripts, remembered)
      }
    }
    state.all = list
  },
  upsertSavedScript(state, script) {
    ;(state.all || []).forEach((caseType) => applySavedScript(caseType, script))
    applySavedScript(state.current, script)
  },
  "route/ROUTE_CHANGED"(state, data) {
    if (data.to.name === "CaseConfig") {
      setCurrentCaseTemplate(state, { caseTypeId: data.to.params.case_type_id })
    }
  },
  createScript(state, { script, caseTypeId }) {
    const allCaseTypes = state.all
    const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(caseTypeId, 10))
    if (caseTypeIndex >= 0) {
      allCaseTypes[caseTypeIndex].scripts.push(script)
      Vue.set(state, "all", allCaseTypes)
    }
  },
  removeScript(state, data) {
    const allCaseTypes = state.all
    const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(data.caseTypeId, 10))
    if (caseTypeIndex >= 0) {
      const caseType = allCaseTypes[caseTypeIndex]

      const scriptIndex = caseType.scripts.findIndex((script) => parseInt(script.id, 10) === parseInt(data.scriptId, 10))
      if (scriptIndex >= 0) {
        caseType.scripts.splice(scriptIndex, 1)
        allCaseTypes[caseTypeIndex] = caseType
        Vue.set(state, "all", allCaseTypes)
      }
    }
  },
  createEmailTemplate(state, { emailTemplate, caseTypeId }) {
    const allCaseTypes = state.all
    const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(caseTypeId, 10))
    if (caseTypeIndex >= 0) {
      // Ensure emailTemplates array exists before pushing
      if (!allCaseTypes[caseTypeIndex].emailTemplates) {
        Vue.set(allCaseTypes[caseTypeIndex], "emailTemplates", [])
      }
      allCaseTypes[caseTypeIndex].emailTemplates.push(emailTemplate)
      Vue.set(state, "all", allCaseTypes)
      
      // Also update current case type if it matches
      if (state.current && parseInt(state.current.id, 10) === parseInt(caseTypeId, 10)) {
        if (!state.current.emailTemplates) {
          Vue.set(state.current, "emailTemplates", [])
        }
        state.current.emailTemplates.push(emailTemplate)
        Vue.set(state, "current", state.current)
      }
    }
  },
  removeEmailTemplate(state, data) {
    const allCaseTypes = state.all
    const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(data.caseTypeId, 10))
    if (caseTypeIndex >= 0) {
      const caseType = allCaseTypes[caseTypeIndex]
      const emailIndex = caseType.emailTemplates.findIndex((emailTemplate) => parseInt(emailTemplate.id, 10) === parseInt(data.emailTemplateId, 10))
      if (emailIndex >= 0) {
        caseType.emailTemplates.splice(emailIndex, 1)
        allCaseTypes[caseTypeIndex] = caseType
        Vue.set(state, "all", allCaseTypes)
      }
    }
  },
  setCaseTypeBuckets(state, data) {
    const buckets = data.buckets
    if (!buckets || !Array.isArray(buckets)) return

    const caseTypes = state.all
    const caseTypeIndex = caseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(data.caseTypeId, 10))
    if (caseTypeIndex < 0) return

    const caseType = caseTypes[caseTypeIndex]
    const caseTypeBuckets = caseType.buckets

    // Always replace with the full list from the API so all buckets (e.g. 1218) display.
    // Previously we only updated stats for buckets already in caseType.buckets, so buckets
    // that existed only in the API response (e.g. from direct getBucketsByCaseType) never appeared.
    Vue.set(caseType, "buckets", buckets)
  },
  setCaseTypeTemplatedFolders(state, { caseTypeId, templatedFolders }) {
    if (caseTypeId && templatedFolders) {
      const allCaseTypes = state.all
      const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(caseTypeId, 10))

      if (caseTypeIndex >= 0) {
        const caseType = allCaseTypes[caseTypeIndex]
        Vue.set(caseType, "templatedFolders", templatedFolders)
      }
    }
  },
  deleteCaseTypeTemplatedFolder(state, { caseTypeId, folderId }) {
    if (caseTypeId && folderId) {
      const allCaseTypes = state.all
      const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(caseTypeId, 10))

      if (caseTypeIndex >= 0) {
        const caseType = allCaseTypes[caseTypeIndex]
        const templatedFolders = caseType.templatedFolders || []
        const updatedTemplatedFolders = templatedFolders.filter((f) => f.id !== folderId)
        Vue.set(caseType, "templatedFolders", updatedTemplatedFolders)
      }
    }
  },
  updateCaseTypeTemplatedFolder(state, { caseTypeId, updatedFolder }) {
    if (caseTypeId && updatedFolder) {
      const allCaseTypes = state.all
      const caseTypeIndex = allCaseTypes.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(caseTypeId, 10))

      if (caseTypeIndex >= 0) {
        const caseType = allCaseTypes[caseTypeIndex]
        const folderIndex = caseType.templatedFolders.findIndex((f) => f.id === updatedFolder.id)
        if (folderIndex !== -1) {
          const updatedTemplatedFolders = Object.assign([], caseType.templatedFolders)
          updatedTemplatedFolders[folderIndex] = updatedFolder
          Vue.set(caseType, "templatedFolders", updatedTemplatedFolders)
        }
      }
    }
  },
  setBucketGroupBuckets(state, { newBuckets, caseTypeId, bucketGroupId }) {
    const caseTypeIndex = state.all.findIndex((caseType) => parseInt(caseType.id, 10) === parseInt(caseTypeId, 10))
    if (caseTypeIndex !== -1) {
      const caseType = state.all[caseTypeIndex]
      const bucketGroupIndex = caseType.bucketGroups.findIndex((bg) => parseInt(bg.id, 10) === parseInt(bucketGroupId, 10))
      if (caseTypeIndex !== -1) {
        Vue.set(caseType.bucketGroups[bucketGroupIndex], "buckets", newBuckets)
      }
    }
  },
}
